import {
  Badge,
  Card,
  CardContent,
  Separator,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '@growdash/design-system/atoms';
import { Button } from '@growdash/design-system/atoms/button.js';
import CoverageMap from '@growdash/growintel/components/molecules/coverage-map';
import { Branch } from '@growdash/growintel/generated';
import useCurrency from '@growdash/growintel/hooks/useCurrency';
import { useGrowintelService } from '@growdash/growintel/hooks/useGrowintelService';
import { useParams } from '@growdash/growintel/hooks/usePathname';
import { useQuery } from '@tanstack/react-query';

import GrowintelImage from '@growdash/growintel/components/atoms/growintel-image';
import {
  ChevronLeft,
  Clock,
  Coffee,
  DollarSign,
  LayoutDashboard,
  MapPin,
  Star,
  Utensils,
} from 'lucide-react';
import React from 'react';
import { useNavigate } from 'react-router-dom';
import MenuItemsView from './_components/MenuItemsView';
import { RestaurantSkeleton } from './skeleton';

type Props = {};

export default function RestaurantDetailPage({}: Props) {
  const params = useParams();
  const navigate = useNavigate();
  const service = useGrowintelService();

  const tabs = [
    {
      value: 'overview',
      label: 'Overview',
      icon: LayoutDashboard,
    },
    {
      value: 'menu',
      label: 'Menu',
      icon: Coffee,
    },
  ] as const;

  type TabValue = (typeof tabs)[number]['value'];

  const [currentTab, setCurrentTab] = React.useState<TabValue>('overview');

  const { data: profileData, isFetching: profileLoading } = useQuery({
    queryKey: ['restaurant', params.id],
    queryFn: async (): Promise<Branch | undefined> => {
      if (!params.id) return;
      const response = await service?.branchesAPI.getBranchBranchesIdGet(
        params.id,
      );
      return response?.data;
    },
  });

  const currency = useCurrency();

  if (profileLoading) {
    return (
      <main className="container mx-auto px-4 py-8 max-w-5xl">
        <RestaurantSkeleton />
      </main>
    );
  }

  return (
    <>
      <div className="col-span-12 ">
        <div className="mb-4">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate(-1)}
            className="flex items-center gap-1"
          >
            <ChevronLeft className="h-4 w-4" />
            Back
          </Button>
        </div>

        <Tabs
          value={currentTab}
          onValueChange={setCurrentTab as (value: string) => void}
        >
          <TabsList className="col-span-3 mb-4">
            {tabs.map((tab) => (
              <TabsTrigger
                key={tab.value}
                value={tab.value}
                className="flex items-center gap-1.5"
              >
                {tab.icon && <tab.icon size={16} />}
                {tab.label}
              </TabsTrigger>
            ))}
          </TabsList>
          <TabsContent value="overview" className="animate-fade-in">
            <div className="w-full grid grid-cols-3 gap-8 gap-y-4">
              <div className="col-span-2 max-lg:col-span-3">
                <div className="flex flex-col gap-6">
                  <div className="flex flex-col gap-4">
                    <Card className="p-4 flex gap-4 max-lg:flex-col">
                      <GrowintelImage
                      type='restaurant'
                        src={profileData?.branch_image ?? ''}
                        className="w-44 object-contain border border-secondary"
                      />

                      <div>
                        <h2 className="text-xl font-semibold mb-4">
                          {profileData?.brand_name}
                        </h2>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div className="flex  gap-2">
                            <MapPin
                              size={16}
                              className="mt-1 text-muted-foreground"
                            />
                            <div>
                              <p className="text-sm font-medium">Location</p>
                              <p className="text-sm text-muted-foreground">
                                {profileData?.area}, {profileData?.city}
                              </p>
                            </div>
                          </div>
                          <div className="flex gap-2">
                            <Clock
                              size={16}
                              className="mt-1 text-muted-foreground"
                            />
                            <div>
                              <p className="text-sm font-medium">
                                Delivery Time
                              </p>
                              <p className="text-sm text-muted-foreground">
                                {profileData?.delivery_time || 'N/A'}{' '}
                                {profileData?.delivery_time ? 'minutes' : ''}
                              </p>
                            </div>
                          </div>
                          <div className="flex  gap-2">
                            <Star size={16} className="mt-1 text-amber-500" />
                            <div>
                              <p className="text-sm font-medium">Rating</p>
                              <p className="text-sm text-muted-foreground">
                                {profileData?.rating_score || 'N/A'}{' '}
                                {profileData?.rating_count || ''}{' '}
                                {profileData?.rating_count ? 'reviews' : ''}
                              </p>
                            </div>
                          </div>
                          <div className="flex  gap-2">
                            <DollarSign
                              size={16}
                              className="mt-1 text-muted-foreground"
                            />
                            <div>
                              <p className="text-sm font-medium">
                                Average Price
                              </p>
                              <p className="text-sm text-muted-foreground">
                                {profileData?.avg_price
                                  ? `${currency} ${profileData?.avg_price?.toFixed(2)}`
                                  : 'N/A'}
                              </p>
                            </div>
                          </div>
                          <div className="flex  gap-2">
                            <Utensils
                              size={16}
                              className="mt-1 text-muted-foreground"
                            />
                            <div>
                              <p className="text-sm font-medium">Cuisines</p>
                              <p className="text-sm text-muted-foreground">
                                {profileData?.cuisines}
                              </p>
                            </div>
                          </div>
                        </div>
                      </div>
                    </Card>
                  </div>

                  <Card>
                    <CardContent className="p-6">
                      <h2 className="text-xl font-semibold mb-4">
                        Price Information
                      </h2>
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <p className="text-sm font-medium">Average Price</p>
                          <p className="text-2xl font-bold">
                            {profileData?.avg_price?.toFixed(2)} {currency}
                          </p>
                        </div>
                        <div>
                          <p className="text-sm font-medium">Median Price</p>
                          <p className="text-2xl font-bold">
                            {profileData?.median_price?.toFixed(2)} {currency}
                          </p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </div>
              </div>
              <div className="flex flex-col gap-6 col-span-1 max-lg:col-span-3">
                <Card>
                  <CardContent className="p-6">
                    <h2 className="text-xl font-semibold mb-4">Location</h2>
                    <div className="h-64 rounded-lg overflow-hidden">
                      <CoverageMap
                        lng={
                          profileData?.longitude
                            ? Number(profileData?.longitude)
                            : 0
                        }
                        ltd={
                          profileData?.latitude
                            ? Number(profileData?.latitude)
                            : 0
                        }
                      />
                    </div>
                    <p className="mt-3 text-sm text-muted-foreground">
                      {profileData?.area}, {profileData?.city},{' '}
                      {profileData?.country_code}
                    </p>
                  </CardContent>
                </Card>

                <Card>
                  <CardContent className="p-6">
                    <h2 className="text-xl font-semibold mb-4">
                      Delivery Information
                    </h2>
                    <div className="flex items-center gap-2 mb-3">
                      <div className="bg-primary/10 p-2 rounded-full">
                        <Clock className="h-5 w-5 text-primary" />
                      </div>
                      <div>
                        <p className="text-sm font-medium">Delivery Time</p>
                        <p className="text-lg font-bold">
                          {profileData?.delivery_time || '--'}{' '}
                          {profileData?.delivery_time ? 'minutes' : ''}
                        </p>
                      </div>
                    </div>
                    <Separator className="my-3" />
                    <p className="text-sm text-muted-foreground">
                      Aggregator:{' '}
                      <Badge variant="secondary" className="text-sm">
                        {profileData?.aggregator}
                      </Badge>
                    </p>
                  </CardContent>
                </Card>
              </div>
            </div>
          </TabsContent>
          <TabsContent value="menu">
            <MenuItemsView branchId={params?.id} />
          </TabsContent>
        </Tabs>
      </div>
    </>
  );
}
